import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-xs tracking-[0.3em] text-amber-500 uppercase mb-4 block">
              GET IN TOUCH
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white tracking-tighter mb-6">
              LET'S<br />
              <span className="text-amber-500">CONNECT</span>
            </h1>
            <p className="font-body text-lg text-zinc-300 leading-relaxed">
              Have questions about a project? Want to schedule a shop visit? Drop us a line and we'll get back to you as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-zinc-900 border border-white/10 p-8 md:p-10">
                <h2 className="font-heading text-3xl text-white mb-6">SEND A MESSAGE</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                      YOUR NAME *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                      EMAIL ADDRESS *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                      PHONE NUMBER
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                      YOUR MESSAGE *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 min-h-[150px] resize-none"
                      placeholder="Tell us about your project or question..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-500 text-black font-heading text-lg tracking-wide py-6 rounded-none hover:bg-amber-400 transition-all duration-200 disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "SENDING..." : "SEND MESSAGE"}
                    <Send className="ml-2" size={20} />
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Map */}
              <div className="bg-zinc-900 border border-white/10 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d83347.3!2d-121.98!3d49.15!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5484573a3f6bd861%3A0x5151257cb8a4abd8!2sChilliwack%2C%20BC!5e0!3m2!1sen!2sca!4v1699999999999"
                  width="100%"
                  height="250"
                  style={{ border: 0, filter: 'grayscale(100%) invert(90%) contrast(90%)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Chilliwack, BC"
                ></iframe>
              </div>

              {/* Address */}
              <div className="bg-zinc-900 border border-white/10 p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white mb-2">LOCATION</h3>
                    <p className="font-body text-zinc-400">
                      Chilliwack, BC
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-zinc-900 border border-white/10 p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white mb-2">CALL US</h3>
                    <a href="tel:778-344-4374" className="font-body text-zinc-400 hover:text-amber-500 transition-colors">778-344-4374</a>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-zinc-900 border border-white/10 p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white mb-2">EMAIL US</h3>
                    <a href="mailto:martensmotorco@gmail.com" className="font-body text-zinc-400 hover:text-amber-500 transition-colors">martensmotorco@gmail.com</a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-zinc-900 border border-white/10 p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white mb-2">SHOP HOURS</h3>
                    <div className="font-body text-zinc-400 space-y-1">
                      <p>Monday - Friday: 8am - 6pm</p>
                      <p>Saturday: 9am - 3pm</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div className="bg-navy p-8">
                <div className="flex items-center gap-4">
                  <Instagram size={24} className="text-amber-500" />
                  <div>
                    <h3 className="font-heading text-xl text-white mb-1">FOLLOW THE BUILD</h3>
                    <button 
                      onClick={() => window.open('https://instagram.com/martensmotorco', '_blank', 'noopener,noreferrer')}
                      className="font-body text-amber-500 hover:text-amber-400 transition-colors"
                    >
                      @martensmotorco
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote CTA */}
      <section className="py-16 bg-zinc-950 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-4">
              READY TO START YOUR PROJECT?
            </h2>
            <p className="font-body text-zinc-400 mb-6">
              Tell us about your vision and let's start the conversation.
            </p>
            <Link to="/quote">
              <Button 
                className="bg-amber-500 text-black font-heading text-lg tracking-wide px-8 py-5 rounded-none hover:bg-amber-400 transition-all duration-200"
                data-testid="contact-quote-btn"
              >
                LET'S GET STARTED
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
